import {inject, Injectable} from '@angular/core';
import {GroupVoucherCode, WineProduct} from '@core/interfaces/generated/graphql';
import {Pricing} from '@core/services/product.service';
import {PriceLine, PriceLinePrefix} from '@layout/components/product/price-table/price-table.component';
import {StoreConfigService} from '@core/services/store-config.service';

@Injectable({
  providedIn: 'root'
})
export class ProductPriceService {
  private storeConfigService = inject(StoreConfigService);
  currencyCode = this.storeConfigService.currencyCode;


  getUnitName(product: WineProduct) {
    return product?.meta?.units?.shortName;
  }

  /**
   * Calculate discount percentage by comparing highest vs lowest price in price table
   * The highest price comes from amountIncVat, the lowest from discount.amountIncVat
   * @param product The product to calculate discount for
   * @returns Discount percentage as a whole number (0-100), or 0 if no discount
   */
  public calculateDiscountPercentage(product: WineProduct | undefined): number {
    if (!product?.priceTable?.pricings) {
      return 0;
    }

    const pricings = product.priceTable.pricings;

    // Get all regular prices (amountIncVat)
    const regularPrices = pricings
      .filter(p => p?.amountIncVat && p.amountIncVat > 0)
      .map(p => p!.amountIncVat!);

    // Get all discount prices (discount.amountIncVat)
    const discountPrices = pricings
      .filter(p => p?.discount?.amountIncVat && p.discount.amountIncVat > 0)
      .map(p => p!.discount!.amountIncVat!);

    if (regularPrices.length === 0 || discountPrices.length === 0) {
      return 0;
    }

    // Calculate discount percentage: (highest regular - lowest discount) / highest
    const highestPrice = Math.max(...regularPrices);
    const lowestPrice = Math.min(...discountPrices);
    const percentage = ((highestPrice - lowestPrice) / highestPrice) * 100;

    return Math.floor(percentage);
  }

  /**
   * Returns true while the campaign to hide the regular 1-pcs strikethrough price is active.
   * Campaign period: active before 2026-03-09 (reverts to normal from 2026-03-09 onwards).
   */
  protected isCampaignActive(): boolean {
    return new Date() < new Date('2026-03-09');
  }

  public priceToPriceLines(price: Pricing, product: WineProduct, hideRegularOnePcsPrice = false, isCustomerLinked = false): PriceLine[] | undefined {
    const lines = [] as PriceLine[];
    const label = $localize`${price.minQty} ${this.getUnitName(product) || ''} pr. stk.`;
    const isDiscounted = !!price.discount || !!price.source;
    const showVatText = price.showVatText;

    let defaultPriceIncVat = price?.amountIncVat as string | number | undefined;

    if (!defaultPriceIncVat || !this.currencyCode()) {
      return undefined;
    }

    if (typeof defaultPriceIncVat === 'string') {
      defaultPriceIncVat = parseInt(defaultPriceIncVat, 10);
    }

    let bestPriceIncVat = price.discount?.amountIncVat || defaultPriceIncVat;
    let prefix: PriceLinePrefix | undefined = undefined;

    if (isDiscounted) {
      if (price?.source && isCustomerLinked) {
        prefix = [$localize`DIN`, $localize`PRIS`];
      } else if (price?.source) {
        prefix = [$localize`PRIS`, price.source];
      } else {
        prefix = [$localize`BEDSTE`, $localize`PRIS`];
      }
    }

    const applyBestPriceCampaign = hideRegularOnePcsPrice && price.minQty === 1;

    if (!applyBestPriceCampaign && isDiscounted && price.strikeThrough && price.minQty === 1) {
      lines.push({
        isDiscounted: false,
        label,
        currencyCode: this.currencyCode() ?? '',
        price: defaultPriceIncVat,
        isStriked: true,
        qty: price.minQty,
        unitName: this.getUnitName(product),
      })
    }

    const effectiveIsDiscounted = applyBestPriceCampaign ? true : isDiscounted;
    const effectivePrefix: PriceLinePrefix | undefined = applyBestPriceCampaign && !prefix
      ? [$localize`BEDSTE`, $localize`PRIS`]
      : prefix;

    lines.push({
      isDiscounted: effectiveIsDiscounted,
      label,
      currencyCode: this.currencyCode() ?? '',
      price: bestPriceIncVat,
      prefix: effectivePrefix,
      showVatText,
      isStriked: effectiveIsDiscounted ? !!price.discount?.strikeThrough : price.strikeThrough,
      qty: price.minQty,
      unitName: this.getUnitName(product),
    })

    return lines;
  }

  formatPriceLines(groupVoucher: GroupVoucherCode | null, product: WineProduct, prices: Pricing[], isCustomerLinked = false) {
    const lines = [] as PriceLine[];

    const hasQuantityPrices = prices.some(p => (p.minQty ?? 1) > 1);
    const hideRegularOnePcsPrice = this.isCampaignActive() && !hasQuantityPrices;

    const mapped = prices.map(price => this.priceToPriceLines(price, product, hideRegularOnePcsPrice, isCustomerLinked)).reduce((prev, curr) => {
      if (curr) {
        prev?.push(...curr);
      }
      return prev
    }, []) as PriceLine[];

    const suggestedRetailPrice = (product as WineProduct).suggestedRetailPrice;
    if (suggestedRetailPrice && groupVoucher && groupVoucher.showSuggestedRetailPrice) {
      return this.getSuggestedRetailLines(mapped, suggestedRetailPrice);
    }

    lines.push(...mapped);

    return lines;
  }

  protected getSuggestedRetailLines(mapped: PriceLine[], suggestedRetailPrice: string) {
    const lines = [] as PriceLine[];
    lines.push({
      isDiscounted: false,
      label: $localize`Vejl. salgspris`,
      price: parseInt(suggestedRetailPrice, 10),
      currencyCode: this.storeConfigService.currencyCode() ?? '',
    });

    const price = mapped.pop();
    if (price) {
      lines.push(price)
    }
    return lines;
  }
}
